import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { API } from '../Common/Api';
import { ErrorToast, SuccessToast } from '../Utils/toast';
import { Button } from '../Common/Button';

// Shared "Edit user" modal for agent / dealer / distributor rows.
// Edits email + the "view transactions" permission. `roleLabel` is copy text only.
// FASTag Activation (removed): previously also accepted `showActivation = true` and rendered a toggle.
const EditUserModal = ({ user, roleLabel = 'user', /* showActivation = true, */ showEmail = true, onClose, onSaved }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  // const [canActivate, setCanActivate] = useState(true);
  const [canViewTxns, setCanViewTxns] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setEmail(user.email || '');
      setName(user.username || '');
      setIsActive(user.isActive !== false);
      // setCanActivate(user.canActivateFastag !== false);
      setCanViewTxns(user.canViewTransactions !== false);
    }
  }, [user]);

  if (!user) return null;

  const handleSave = async () => {
    if (!name || name.trim() === '') {
      ErrorToast('Please enter a name');
      return;
    }
    if (showEmail && email && email.trim() !== '' && !/^\S+@\S+\.\S+$/.test(email.trim())) {
      ErrorToast('Please enter a valid email address');
      return;
    }
    setSaving(true);
    try {
      const params = {
        userId: user._id,
        username: name.trim(),
        isActive,
        canViewTransactions: canViewTxns,
      };
      // Only the owner (e.g. a dealer for its agent) may set the email.
      if (showEmail) params.email = email.trim();
      // if (showActivation) params.canActivateFastag = canActivate;

      const res = await API.EditUserPermissions(params);
      if (res?.status === 200 && res?.success) {
        SuccessToast(res?.message || 'Updated successfully');
        onClose();
        if (onSaved) onSaved();
      } else {
        ErrorToast(res?.message || 'Failed to update');
      }
    } catch (e) {
      ErrorToast('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const Toggle = ({ checked, onToggle }) => (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onToggle}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? 'bg-green-500' : 'bg-gray-300'}`}
    >
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-1'}`} />
    </button>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-lg">
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Edit {roleLabel.charAt(0).toUpperCase() + roleLabel.slice(1)}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{user.username} • {user.mobileNo}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Name (editable) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={`Enter ${roleLabel} name`}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          {/* Email (editable) — only for owners allowed to manage it */}
          {showEmail && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email address"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
          )}

          {/*
          FASTag Activation permission (hidden for agents)
          {showActivation && (
            <div className="flex items-center justify-between border border-gray-200 rounded-md px-3 py-3">
              <div>
                <p className="text-sm font-medium text-gray-800">FASTag Activation</p>
                <p className="text-xs text-gray-500">Allow this {roleLabel} to activate FASTags</p>
              </div>
              <Toggle checked={canActivate} onToggle={() => setCanActivate((v) => !v)} />
            </div>
          )}
          */}

          {/* Active / Deactivate status */}
          <div className="flex items-center justify-between border border-gray-200 rounded-md px-3 py-3">
            <div>
              <p className="text-sm font-medium text-gray-800">Account Status</p>
              <p className="text-xs text-gray-500">
                {isActive
                  ? `This ${roleLabel} is active and can sign in`
                  : `This ${roleLabel} is deactivated and cannot sign in`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-medium ${isActive ? 'text-green-600' : 'text-gray-400'}`}>
                {isActive ? 'Active' : 'Inactive'}
              </span>
              <Toggle checked={isActive} onToggle={() => setIsActive((v) => !v)} />
            </div>
          </div>

          {/* View Transactions permission */}
          <div className="flex items-center justify-between border border-gray-200 rounded-md px-3 py-3">
            <div>
              <p className="text-sm font-medium text-gray-800">View Transactions</p>
              <p className="text-xs text-gray-500">Allow this {roleLabel} to view activated FASTag transactions</p>
            </div>
            <Toggle checked={canViewTxns} onToggle={() => setCanViewTxns((v) => !v)} />
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md">Cancel</button>
          <Button
            label={saving ? 'Saving...' : 'Save Changes'}
            onClick={handleSave}
            disabled={saving}
            style={{ backgroundColor: '#EF4444' }}
            customClass="!text-white"
          />
        </div>
      </div>
    </div>
  );
};

export default EditUserModal;
